/**
 * settingsState.ts — App preferences (localStorage-backed, synced to Supabase offline-first).
 */

import { STORAGE_KEYS } from "../constants";

// ── Voice guidance (TTS) ──────────────────────────────────────────────────────
export function getTTSEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.TTS_ENABLED) !== "false";
  } catch {
    return true;
  }
}

export function setTTSEnabled(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEYS.TTS_ENABLED, String(value));
  } catch {
    // localStorage unavailable
  }
}

// ── Bells enabled (global mute) ───────────────────────────────────────────────
export function getBellsEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.BELLS_ENABLED) !== "false";
  } catch {
    return true;
  }
}

export function setBellsEnabled(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEYS.BELLS_ENABLED, String(value));
  } catch {
    // localStorage unavailable (private browsing, storage quota)
  }
}

// ── Whispers ──────────────────────────────────────────────────────────────────
export function getWhispersEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.WHISPERS_ENABLED) !== "false";
  } catch {
    return true;
  }
}

export function setWhispersEnabled(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEYS.WHISPERS_ENABLED, String(value));
  } catch {
    // localStorage unavailable (private browsing, storage quota)
  }
}

// ── Preferred tradition ───────────────────────────────────────────────────────
export type PreferredTradition = "zen" | "vipassana" | null;

export function getPreferredTradition(): PreferredTradition {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.PREFERRED_TRADITION);
    if (v === "zen" || v === "vipassana") return v;
    return null;
  } catch {
    return null;
  }
}

export function setPreferredTradition(value: PreferredTradition): void {
  try {
    if (value === null) {
      localStorage.removeItem(STORAGE_KEYS.PREFERRED_TRADITION);
    } else {
      localStorage.setItem(STORAGE_KEYS.PREFERRED_TRADITION, value);
    }
  } catch {
    // localStorage unavailable (private browsing, storage quota)
  }
}

// ── Audio reflection upload ───────────────────────────────────────────────────
// Off by default — feature is available but hidden until the user opts in.
export function getAudioReflectionEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.AUDIO_REFLECTION_ENABLED) === "true";
  } catch {
    return false;
  }
}

export function setAudioReflectionEnabled(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEYS.AUDIO_REFLECTION_ENABLED, String(value));
  } catch {
    // localStorage unavailable (private browsing, storage quota)
  }
}

// ── Snapshot (read all settings at once) ─────────────────────────────────────
export type SettingsSnapshot = ReturnType<typeof getSettingsSnapshot>;

export function getSettingsSnapshot() {
  return {
    bellsEnabled: getBellsEnabled(),
    ttsEnabled: getTTSEnabled(),
    whispersEnabled: getWhispersEnabled(),
    preferredTradition: getPreferredTradition(),
    audioReflectionEnabled: getAudioReflectionEnabled(),
  };
}
